import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { AiChat, useAsStreamAdapter, useAiChatApi } from '@nlux/react';

import { useContext as useUsersContext } from 'components/Users/Context';

import { useContext } from './Context';

export default function Chat({ onOpenVideo }) {
  const { state: stateUsers } = useUsersContext();
  const { dispatch, actions, state } = useContext();

  const [chatId, setChatId] = useState(state.currentChat?.session_id);

  const member = stateUsers.user.attributes.specific;

  const sessionAttributes = {
    environment: stateUsers.user.attributes.environment,
    user_id: stateUsers.user.id,
    user_email: member.email,
    user_name: member.first_name,
    user_full_name: member.full_name,
  };

  const streamText = async (message, observer, options) => {
    const chat = options?.aiChatProps?.currentChat;

    const socket = new WebSocket(gon.ai_wss_api_base_url);

    socket.onmessage = (event) => {
      if (event.data !== '') {
        const data = JSON.parse(event.data);
        if (data.chunk) {
          observer.next(data.chunk);
        } else if (data.done) {
          observer.complete();
          if (data.is_title_needed === '1') {
            actions.generateChatTitle(dispatch, state.token, data.chat_id);
          } else {
            actions.updateChatTitle(dispatch, data.chat_id, data.chat_title);
          }
        }
      }
    };

    socket.onclose = () => observer.complete();
    socket.onerror = (error) => observer.error(error);

    const sendMessage = (sessionId) => {
      if (socket.readyState !== WebSocket.OPEN) {
        setTimeout(() => {
          sendMessage(sessionId);
        }, 1000);

        return;
      }

      const payload = {
        action: 'sendMessage',
        session_id: sessionId,
        save: true,
        message,
        session_attributes: JSON.stringify(sessionAttributes),
      };
      if (state.avatar.attributes.is_inline) {
        payload.inline = true
      }
      socket.send(JSON.stringify(payload));
    };

    if (chat === undefined) {
      actions.createChat({
        token: state.token,
        sessionType: 'chat',
        avatarId: state.avatar.id,
        avatarName: state.avatar.attributes.name,
        bedrockAvatarId: state.avatar.attributes.bedrock_agent,
        bedrockAvatarAliasId: state.avatar.attributes.bedrock_alias,
        callback: (sessionId) => {
          console.log('init chat', sessionId);
          setChatId(sessionId);
          actions.setCurrentChatId(dispatch, sessionId);
          actions.load(dispatch, state.token);
          sendMessage(sessionId);
        },
      });
    } else {
      sendMessage(chat);
    }
  };

  const adapter = useAsStreamAdapter(streamText, []);
  const api = useAiChatApi();

  const assistantPersona = {
    name: state.avatar.attributes.greeting,
    avatar: '/images/default_avatar.png',
    tagline: (<div>
      <p>{state.avatar.attributes.description}</p>
      <p>Start a video session with your Coach instantly by clicking the on the image below:</p>
      <div onClick={() => onOpenVideo()}>
        <img
          className="video-preview"
          src={state.avatar.attributes.preview_image_url}
          alt="preview"
        />
      </div>
    </div>),
  };

  const memberAvatar = member.avatar_present ? decodeURI(member.decode_avatar_url) : '/images/default_avatar.png';

  const memberAvatarNode = (
    <div className="nlux-comp-avatarContainer">
      <div className="nlux-comp-avatarPicture" style={{ backgroundImage: `url(${memberAvatar})` }} />
    </div>
  );

  const userPersona = {
    name: member.first_name || 'Member',
    avatar: memberAvatarNode,
  };

  const initialConversation = state.currentChat === null ? null
    : state.currentChat.messages.map((message) => ({
      role: message.role === 'user' ? 'user' : 'assistant',
      message: message.content,
    }));

  return (
    <AiChat
      api={api}
      adapter={adapter}
      placeholder="Type a message..."
      currentChat={chatId}
      displayOptions={{}}
      personaOptions={{
        assistant: assistantPersona,
        user: userPersona,
      }}
      conversationOptions={{
        conversationStarters: state.avatar.attributes.prompts.map((prompt) => ({
          label: prompt.message,
          prompt: prompt.message,
        })),
      }}
      composerOptions={{ placeholder: 'Type a message...' }}
      initialConversation={initialConversation}
    />
  );
}

Chat.propTypes = {
  onOpenVideo: PropTypes.func.isRequired,
};
